/**
 * Company Stats Panel
 *
 * Displays company-level statistics and auto-profiling data.
 * Shows commodity profile, trade routes, partnerships, and activity summary.
 */
import {
    Package,
    Globe02,
    Users01,
    Container,
    TrendUp01,
    TrendDown01,
    ArrowRight,
} from '@untitledui/icons';
import { useTranslation } from 'react-i18next';
import { Badge } from '@/components/base/badges/badges';
import { ProgressBar } from '@/components/base/progress-indicators/progress-indicators';
import { Table, TableCard } from '@/components/application/table/table';
import { useCompanyProfile, type TopHsCode, type TopTradeRoute } from '@/hooks/api/use-company-profile';

interface StatCardProps {
    title: string;
    value: string | number;
    subtitle?: string;
    icon: React.ComponentType<{ className?: string }>;
    iconColor: string;
    iconBgColor: string;
    children?: React.ReactNode;
}

const StatCard = ({ title, value, subtitle, icon: Icon, iconColor, iconBgColor, children }: StatCardProps) => (
    <div className="rounded-xl bg-primary p-5 shadow-xs ring-1 ring-secondary">
        <div className="flex items-start justify-between">
            <div>
                <p className="text-sm font-medium text-tertiary">{title}</p>
                <p className="mt-1 text-2xl font-semibold text-primary">{value}</p>
                {subtitle && (
                    <p className="mt-0.5 text-sm text-quaternary">{subtitle}</p>
                )}
            </div>
            <div className={`rounded-lg p-2.5 ${iconBgColor}`}>
                <Icon className={`size-5 ${iconColor}`} />
            </div>
        </div>
        {children && <div className="mt-4">{children}</div>}
    </div>
);

// Format currency
const formatCurrency = (value: number): string => {
    if (value >= 1000000) {
        return `$${(value / 1000000).toFixed(1)}M`;
    }
    if (value >= 1000) {
        return `$${(value / 1000).toFixed(0)}K`;
    }
    return `$${value.toFixed(0)}`;
};

// Format number with abbreviation
const formatNumber = (value: number): string => {
    if (value >= 1000000) {
        return `${(value / 1000000).toFixed(1)}M`;
    }
    if (value >= 1000) {
        return `${(value / 1000).toFixed(0)}K`;
    }
    return value.toLocaleString();
};

// Get trading pattern label and color
const getTradingPatternBadge = (pattern: string) => {
    switch (pattern) {
        case 'primarily_exporter':
            return { label: 'Primarily Exporter', color: 'brand' as const, icon: TrendUp01 };
        case 'primarily_importer':
            return { label: 'Primarily Importer', color: 'success' as const, icon: TrendDown01 };
        case 'mixed_trader':
            return { label: 'Mixed Trader', color: 'warning' as const, icon: ArrowRight };
        default:
            return { label: 'Unknown', color: 'gray' as const, icon: ArrowRight };
    }
};

// Get volume tier label and color
const getVolumeTierBadge = (tier: string) => {
    switch (tier) {
        case 'high_volume':
            return { label: 'High Volume', color: 'success' as const };
        case 'medium_volume':
            return { label: 'Medium Volume', color: 'warning' as const };
        case 'low_volume':
            return { label: 'Low Volume', color: 'gray' as const };
        default:
            return { label: 'No Activity', color: 'gray' as const };
    }
};

export function CompanyStatsPanel() {
    const { t } = useTranslation(['pages:dashboard', 'common']);
    const { data: profile, isLoading, error } = useCompanyProfile(true);

    // Loading state
    if (isLoading) {
        return (
            <div className="space-y-6">
                <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4">
                    {[...Array(4)].map((_, i) => (
                        <div key={i} className="rounded-xl bg-primary p-5 shadow-xs ring-1 ring-secondary animate-pulse">
                            <div className="h-4 w-24 bg-tertiary/20 rounded mb-2" />
                            <div className="h-8 w-16 bg-tertiary/20 rounded" />
                        </div>
                    ))}
                </div>
            </div>
        );
    }

    // Error state
    if (error || !profile) {
        return (
            <div className="rounded-xl bg-primary p-8 shadow-xs ring-1 ring-secondary text-center">
                <p className="text-sm text-error-600">Failed to load company profile</p>
            </div>
        );
    }

    const tradingPattern = getTradingPatternBadge(profile.trading_pattern);
    const volumeTier = getVolumeTierBadge(profile.volume_tier);

    return (
        <div className="space-y-6">
            {/* Profile Header */}
            <div className="rounded-xl bg-primary p-6 shadow-xs ring-1 ring-secondary">
                <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
                    <div>
                        <h3 className="text-lg font-semibold text-primary">
                            {t('controlHub.companyProfile.title', { defaultValue: 'Company Profile' })}
                        </h3>
                        <p className="text-sm text-tertiary mt-1">{profile.company_name}</p>
                    </div>
                    {(profile.trading_pattern !== 'unknown' || profile.volume_tier !== 'no_activity') && (
                        <div className="flex flex-wrap items-center gap-2">
                            {profile.trading_pattern !== 'unknown' && (
                                <Badge color={tradingPattern.color} size="md">
                                    <tradingPattern.icon className="size-3.5 mr-1" />
                                    {tradingPattern.label}
                                </Badge>
                            )}
                            {profile.volume_tier !== 'no_activity' && (
                                <Badge color={volumeTier.color} size="md">
                                    {volumeTier.label}
                                </Badge>
                            )}
                        </div>
                    )}
                </div>

                {/* Profile Completeness */}
                <div className="mt-4">
                    <div className="flex items-center justify-between mb-2">
                        <span className="text-sm text-tertiary">
                            {t('controlHub.companyProfile.profileCompleteness', { defaultValue: 'Profile Completeness' })}
                        </span>
                        <span className="text-sm font-medium text-primary">
                            {profile.profile_completeness_score}%
                        </span>
                    </div>
                    <ProgressBar
                        value={profile.profile_completeness_score}
                        min={0}
                        max={100}
                    />
                </div>
            </div>

            {/* Stats Cards Grid */}
            <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4">
                {/* Commodities */}
                <StatCard
                    title={t('controlHub.companyProfile.commodities', { defaultValue: 'Commodities' })}
                    value={profile.unique_commodities}
                    subtitle={`${profile.commodity_shipments} shipments`}
                    icon={Package}
                    iconColor="text-brand-600"
                    iconBgColor="bg-brand-50"
                >
                    <div className="text-sm text-tertiary">
                        Total value: <span className="font-medium text-primary">{formatCurrency(profile.commodity_value_usd)}</span>
                    </div>
                    {profile.hs_chapters_traded && profile.hs_chapters_traded.length > 0 && (
                        <div className="mt-2 flex flex-wrap gap-1">
                            {profile.hs_chapters_traded.slice(0, 4).map((chapter) => (
                                <span key={chapter} className="inline-block px-1.5 py-0.5 text-xs bg-secondary rounded text-tertiary">
                                    HS {chapter}
                                </span>
                            ))}
                            {profile.hs_chapters_traded.length > 4 && (
                                <span className="text-xs text-quaternary">+{profile.hs_chapters_traded.length - 4} more</span>
                            )}
                        </div>
                    )}
                </StatCard>

                {/* Trade Routes */}
                <StatCard
                    title={t('controlHub.companyProfile.tradeRoutes', { defaultValue: 'Trade Routes' })}
                    value={profile.unique_routes}
                    subtitle={`${profile.route_shipments} shipments`}
                    icon={Globe02}
                    iconColor="text-success-600"
                    iconBgColor="bg-success-50"
                >
                    <div className="text-sm text-tertiary">
                        <span className="font-medium text-primary">{profile.origin_countries}</span> origins →{' '}
                        <span className="font-medium text-primary">{profile.destination_countries}</span> destinations
                    </div>
                    <div className="mt-1 text-xs text-quaternary">
                        {formatNumber(profile.total_weight_kg)} kg · {formatNumber(profile.total_volume_cbm)} CBM
                    </div>
                </StatCard>

                {/* Partners */}
                <StatCard
                    title={t('controlHub.companyProfile.partners', { defaultValue: 'Trade Partners' })}
                    value={profile.trade_partners}
                    subtitle={`${profile.active_partners} active`}
                    icon={Users01}
                    iconColor="text-warning-600"
                    iconBgColor="bg-warning-50"
                >
                    <div className="text-sm text-tertiary">
                        Strong: <span className="font-medium text-primary">{profile.strong_partners}</span>
                    </div>
                    <div className="mt-1 text-xs text-quaternary">
                        {formatCurrency(profile.partnership_value_usd)} total value
                    </div>
                </StatCard>

                {/* Container Activity */}
                <StatCard
                    title={t('controlHub.companyProfile.containers', { defaultValue: 'Container Activity' })}
                    value={profile.total_containers}
                    subtitle={`${profile.completed_containers} completed`}
                    icon={Container}
                    iconColor="text-error-600"
                    iconBgColor="bg-error-50"
                >
                    <div className="text-sm text-tertiary">
                        Total value: <span className="font-medium text-primary">{formatCurrency(profile.containers_value_usd)}</span>
                    </div>
                    {profile.total_containers > 0 && (
                        <div className="mt-2">
                            <div className="flex items-center justify-between text-xs text-quaternary mb-1">
                                <span>Completion rate</span>
                                <span>{Math.round((profile.completed_containers / profile.total_containers) * 100)}%</span>
                            </div>
                            <ProgressBar
                                value={(profile.completed_containers / profile.total_containers) * 100}
                                min={0}
                                max={100}
                            />
                        </div>
                    )}
                </StatCard>
            </div>

            {/* Top Items Tables */}
            <div className="grid grid-cols-1 gap-6 lg:grid-cols-2">
                {/* Top HS Codes */}
                {profile.top_hs_codes.length > 0 && (
                    <TableCard.Root>
                        <TableCard.Header
                            title={t('controlHub.companyProfile.topHsCodes', { defaultValue: 'Top HS Codes' })}
                            description="Most traded commodities by value"
                        />
                        <Table>
                            <Table.Header>
                                <Table.Head label="HS Code" isRowHeader />
                                <Table.Head label="Role" />
                                <Table.Head label="Shipments" />
                                <Table.Head label="Value" />
                            </Table.Header>
                            <Table.Body>
                                {profile.top_hs_codes.map((item: TopHsCode) => (
                                    <Table.Row key={`${item.hs_code}-${item.role}`}>
                                        <Table.Cell>
                                            <div className="font-medium text-primary">{item.hs_code}</div>
                                            {item.hs_code_label ? (
                                                <div className="text-xs text-quaternary">{item.hs_code_label}</div>
                                            ) : item.hs_chapter ? (
                                                <div className="text-xs text-quaternary">Chapter {item.hs_chapter}</div>
                                            ) : null}
                                        </Table.Cell>
                                        <Table.Cell>
                                            <Badge color={item.role === 'shipper' ? 'brand' : 'success'} size="sm">
                                                {item.role === 'shipper' ? 'Export' : 'Import'}
                                            </Badge>
                                        </Table.Cell>
                                        <Table.Cell>
                                            <span className="text-sm text-tertiary">{item.shipment_count}</span>
                                        </Table.Cell>
                                        <Table.Cell>
                                            <span className="text-sm font-medium text-primary">
                                                {formatCurrency(item.total_value_usd)}
                                            </span>
                                        </Table.Cell>
                                    </Table.Row>
                                ))}
                            </Table.Body>
                        </Table>
                    </TableCard.Root>
                )}

                {/* Top Trade Routes */}
                {profile.top_trade_routes.length > 0 && (
                    <TableCard.Root>
                        <TableCard.Header
                            title={t('controlHub.companyProfile.topRoutes', { defaultValue: 'Top Trade Routes' })}
                            description="Most active trade corridors by value"
                        />
                        <Table>
                            <Table.Header>
                                <Table.Head label="Route" isRowHeader />
                                <Table.Head label="Shipments" />
                                <Table.Head label="Value" />
                            </Table.Header>
                            <Table.Body>
                                {profile.top_trade_routes.map((item: TopTradeRoute, index: number) => (
                                    <Table.Row key={`${item.origin_country_code}-${item.destination_country_code}-${index}`}>
                                        <Table.Cell>
                                            <div className="flex items-center gap-2">
                                                <span className="font-medium text-primary">{item.origin_country_code}</span>
                                                <ArrowRight className="size-4 text-quaternary" />
                                                <span className="font-medium text-primary">{item.destination_country_code}</span>
                                            </div>
                                        </Table.Cell>
                                        <Table.Cell>
                                            <span className="text-sm text-tertiary">{item.shipment_count}</span>
                                        </Table.Cell>
                                        <Table.Cell>
                                            <span className="text-sm font-medium text-primary">
                                                {formatCurrency(item.total_value_usd)}
                                            </span>
                                        </Table.Cell>
                                    </Table.Row>
                                ))}
                            </Table.Body>
                        </Table>
                    </TableCard.Root>
                )}
            </div>

            {/* Empty state for tables if no data */}
            {profile.top_hs_codes.length === 0 && profile.top_trade_routes.length === 0 && (
                <div className="rounded-xl bg-secondary/50 p-8 text-center">
                    <p className="text-sm text-tertiary">
                        No trading data available yet. Start creating shipments to see your company profile.
                    </p>
                </div>
            )}
        </div>
    );
}
